import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EDigestPreference } from '@/graphql-types/globalTypes';
import { useEditCurrentUserGroupMembership } from '@/hooks/useEditCurrentUserGroupMembership/useEditCurrentUserGroupMembership';
import { useGroupPermissions } from '@/hooks/useGroupPermissions';
import { logError } from '@/imports/logging/ClientLogger';
import {
  BellIcon,
  Edit2Icon,
  MoreHorizontalIcon,
  UserMinusIcon,
} from '@/imports/ui/chakra/feather';
import { UserDigestSettingsModal } from '@/imports/ui/components/UserDigestSettingsModal';

import { GroupHeaderDropdown_GroupFragment } from './graphql-types/GroupHeaderDropdown_GroupFragment';
import {
  GroupHeaderDropdown_UpdateGroupLinkMutation,
  GroupHeaderDropdown_UpdateGroupLinkMutationVariables,
} from './graphql-types/GroupHeaderDropdown_UpdateGroupLinkMutation';

const UPDATE_USER_GROUP_LINK = gql`
  mutation GroupHeaderDropdown_UpdateGroupLinkMutation(
    $input: UpdateUserGroupLinkInput!
  ) {
    updateUserGroupLink(input: $input) {
      data {
        _id
        digestPreference
      }
    }
  }
`;

export const GroupHeaderDropdown: React.FC<{
  group: GroupHeaderDropdown_GroupFragment;
}> & {
  groupFragment: DocumentNode;
} = ({ group }) => {
  const toast = useToast();
  const leaveConfirm = useDisclosure();
  const [digestSettingsModalOpen, setDigestSettingsModalOpen] =
    React.useState(false);
  const router = useRouter();
  const [updateUserGroupLink, { loading }] = useMutation<
    GroupHeaderDropdown_UpdateGroupLinkMutation,
    GroupHeaderDropdown_UpdateGroupLinkMutationVariables
  >(UPDATE_USER_GROUP_LINK, {
    refetchQueries: ['GroupPageHome_groupQuery'],
    awaitRefetchQueries: true,
  });

  const userGroupPermissions = useGroupPermissions(group._id);

  const { createFollow, createFollowLoading, deleteFollow, userLoading } =
    useEditCurrentUserGroupMembership(group);

  const onLeaveGroup = async (): Promise<void> => {
    await deleteFollow();
    router.push('/community/groups');
  };

  const setGroupDigestPreference = async (
    pref: EDigestPreference
  ): Promise<void> => {
    try {
      await updateUserGroupLink({
        variables: {
          input: {
            id: group.currentUserGroupLink?._id,
            data: {
              digestPreference: pref,
            },
          },
        },
      });
      toast({
        status: 'success',
        description: 'Successfully updated group digest preference.',
      });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to update preference.' });
      logError(err);
    }
  };

  if (userGroupPermissions.isLoading || userLoading) {
    return null;
  }

  if (!group.currentUserGroupLink?._id) {
    if (
      !group.currentUserGroupPermission?.canJoin &&
      !userGroupPermissions.canViewSettingsPage
    ) {
      return null;
    } else if (
      group.currentUserGroupPermission?.canJoin &&
      !userGroupPermissions.canViewSettingsPage &&
      !group.settings?.isAutomated
    ) {
      return (
        <Button onClick={createFollow} isLoading={createFollowLoading}>
          Join
        </Button>
      );
    } else if (
      group.currentUserGroupPermission?.canJoin &&
      userGroupPermissions.canViewSettingsPage
    ) {
      return (
        <HStack>
          {!group.settings?.isAutomated && (
            <Button onClick={createFollow} isLoading={createFollowLoading}>
              Join
            </Button>
          )}
          <Menu aria-label="manage group preferences">
            <MenuButton
              as={IconButton}
              variant="outline"
              colorScheme="gray"
              icon={<MoreHorizontalIcon />}
              size="sm"
            />
            <MenuList>
              <NextLink href={`/community/groups/${group._id}/edit/settings`}>
                <MenuItem icon={<Edit2Icon />}>Edit Group</MenuItem>
              </NextLink>
            </MenuList>
          </Menu>
        </HStack>
      );
    }
    return (
      <Menu aria-label="manage group preferences">
        <MenuButton
          as={IconButton}
          variant="outline"
          colorScheme="gray"
          icon={<MoreHorizontalIcon />}
          size="sm"
        />
        <MenuList>
          <NextLink href={`/community/groups/${group._id}/edit/settings`}>
            <MenuItem icon={<Edit2Icon />}>Edit Group</MenuItem>
          </NextLink>
        </MenuList>
      </Menu>
    );
  }

  const effectivePref =
    group.currentUserGroupLink?.digestPreference ||
    group.settings.defaultDigestPref;

  return (
    <>
      <Menu aria-label="manage group preferences">
        <MenuButton
          as={IconButton}
          variant="outline"
          colorScheme="gray"
          icon={<MoreHorizontalIcon />}
          size="sm"
        />
        <Portal>
          <MenuList>
            {userGroupPermissions.canViewSettingsPage && (
              <NextLink href={`/community/groups/${group._id}/edit/settings`}>
                <MenuItem icon={<Edit2Icon />}>Edit Group</MenuItem>
              </NextLink>
            )}
            <MenuItem
              icon={<BellIcon />}
              onClick={() => setDigestSettingsModalOpen(true)}
            >
              Manage My Notifications
            </MenuItem>
            <MenuItem icon={<UserMinusIcon />} onClick={leaveConfirm.onOpen}>
              Leave Group
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
      <ConfirmDialog
        isOpen={leaveConfirm.isOpen}
        onClose={leaveConfirm.onClose}
        onConfirm={onLeaveGroup}
        confirmButtonText="Yes"
        cancelButtonText="No"
        title={`Leave ${group.name} Group?`}
        body={
          "If you leave, you'll lose access to this group's discussions, events, and resources. You'll no longer receive updates or notifications from this group."
        }
        confirmButtonColorScheme="blue"
      />
      <UserDigestSettingsModal
        value={effectivePref}
        isOpen={digestSettingsModalOpen}
        onClose={() => setDigestSettingsModalOpen(false)}
        loading={loading}
        onChange={async (digestPref) =>
          await setGroupDigestPreference(digestPref)
        }
        modalHeader={'Group Digest Settings'}
        formLabel={'User Digest Preference'}
      />
    </>
  );
};
GroupHeaderDropdown.groupFragment = gql`
  fragment GroupHeaderDropdown_GroupFragment on Group {
    _id
    settings {
      defaultDigestPref
      isAutomated
    }
    currentUserGroupLink {
      _id
      digestPreference
    }
    currentUserGroupPermission {
      canJoin
    }
    ...useEditCurrentUserGroupMembership_GroupFragment
  }
  ${useEditCurrentUserGroupMembership.groupFragment}
`;
